import { createSlice } from "@reduxjs/toolkit"
import { NovaEntity, Project } from "@nova/protobuf"
import { loadProject } from "features/map/control/thunks"
import { del, get, set } from "@nova/serializer/src/client/path"
import { projectEntityKeys, ProjectWithMetadata } from "@nova/constants"
import { get as lodashGet } from "nodash"
import { ProtobufEvents } from "@nova/serializer/src/client/events"

export type ClientSocketEvent = ProtobufEvents[keyof ProtobufEvents]["clientPayload"]

const initialState = {
    data: Project.create() as ProjectWithMetadata,
    loaded: false,
    loading: true,
    queue: [] as ClientSocketEvent[],
}

export const slice = createSlice({
    name: "project",
    initialState,
    reducers: {
        handleIncomingSocketEvents: (
            state,
            { payload: { events } }: { payload: { events: ClientSocketEvent[] } }
        ) => {
            flushQueue(events, state.data)
        },
        addEntitySocket: (state, { payload: { data, path } }) => {
            if (state.loading) {
                state.queue.push({ type: "add", data, path })
                return
            }
            let found = false

            if (typeof path.at(-1) === "string") {
                const arr = get(state.data, path) as NovaEntity[] | unknown
                if (Array.isArray(arr)) {
                    const idx = arr.findIndex((e) => e.id === data.id)
                    if (idx !== -1) {
                        found = true
                        Object.assign(arr[idx], data)
                    }
                }
            }
            if (!found) set(state.data as Project, path, data)
        },
        updateEntitySocket: (state, { payload: { path, updates } }) => {
            if (state.loading) {
                state.queue.push({ type: "update", data: updates, path })
                return
            }
            set(state.data, path, updates)
        },
        deleteEntitySocket: (state, { payload: { path } }) => {
            if (state.loading) {
                state.queue.push({ type: "delete", path })
                return
            }
            try {
                del(state.data, path)
            } catch (err) {
                // assuming failed deletes are just 2 people deleting the same thing at the same time, no way to know history
                console.error(err)
            }
        },
        batchAddEntitySocket: (state, { payload: { entities, path } }) => {
            if (state.loading) {
                state.queue.push({ type: "batchAdd", data: entities, path })
                return
            }
            if (typeof path.at(-1) === "string") {
                const arr = get(state.data, path) as NovaEntity[] | unknown
                if (Array.isArray(arr)) {
                    const existingIds = new Map(arr.map((e: NovaEntity, i: number) => [e.id, i]))
                    entities.forEach((entity: ProjectWithMetadata) => {
                        const idx = existingIds.get(entity.id)
                        if (idx !== undefined) {
                            Object.assign(arr[idx], entity)
                        } else {
                            set(state.data, path, entity)
                        }
                    })
                    return
                }
            }
            entities.forEach((entity: ProjectWithMetadata) => {
                set(state.data, path, entity)
            })
        },
        batchUpdateEntitiesSocket: (state, { payload: { path, updates, ids } }) => {
            if (state.loading) {
                state.queue.push({
                    type: "batchEntitiesUpdate",
                    data: updates,
                    path,
                    ids,
                })
                return
            }

            const entities = get(state.data, path) as { id: string }[]
            entities.forEach((e) => {
                if (ids.includes(e["id"])) {
                    Object.assign(e, updates)
                }
            })
        },
        batchUpdateGroupSocket: (state, { payload: { path, updates, groupKey } }) => {
            if (state.loading) {
                state.queue.push({ type: "batchUpdate", data: updates, path, groupKey })
                return
            }
            projectEntityKeys.forEach((key) => {
                if (state.data[key]) {
                    state.data[key].forEach((e) => {
                        const id = lodashGet(path.at(-1), "id")
                        if (
                            groupKey in e &&
                            (e as unknown as Record<string, unknown>)[groupKey] === id
                        ) {
                            Object.assign(e, updates)
                        }
                    })
                }
            })
        },
        replaceEntitySocket: (state, { payload: { path, data } }) => {
            if (state.loading) {
                state.queue.push({ type: "replace", data, path })
                return
            }
            set(state.data as Project, path, data)
        },
        rejectCreate: (state, action) => {
            if (action.payload.data) {
                set(state.data, action.payload.path, action.payload.data)
            } else {
                del(state.data, action.payload.path)
            }
        },
        rejectUpdate: (state, action) => {
            set(state.data, action.payload.path, action.payload.data)
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(loadProject.pending, (state) => {
                state.loaded = false
                state.loading = true
                state.queue = []
                state.data = Project.create() as ProjectWithMetadata
            })
            .addCase(loadProject.fulfilled, (state, action) => {
                state.loaded = true
                state.loading = false
                const project = action.payload
                flushQueue(state.queue, project)
                state.queue = []
                state.data = project
            }),
})

function flushQueue(queue: ClientSocketEvent[], project: ProjectWithMetadata) {
    queue.forEach((item) => {
        switch (item.type) {
            case "add": {
                let found = false
                if (typeof item.path.at(-1) === "string") {
                    try {
                        const arr = get(project, item.path as never) as NovaEntity[] | unknown
                        if (Array.isArray(arr)) {
                            const idx = arr.findIndex(
                                (e) => e.id === (item.data as { id?: string }).id
                            )
                            if (idx !== -1) {
                                found = true
                            }
                        }
                    } catch {}
                }
                if (!found) set(project, item.path, item.data as never)
                break
            }
            case "batchAdd": {
                if (typeof item.path.at(-1) === "string") {
                    try {
                        const arr = get(project, item.path as never) as NovaEntity[] | unknown
                        if (Array.isArray(arr)) {
                            const existingIds = new Map(
                                arr.map((e: NovaEntity, i: number) => [e.id, i])
                            )
                            item.data.entities.forEach((e) => {
                                const idx = existingIds.get((e as NovaEntity).id)
                                if (idx !== undefined) {
                                    Object.assign(arr[idx], e)
                                } else {
                                    set(project, item.path, e as never)
                                }
                            })
                            break
                        }
                    } catch {}
                }
                item.data.entities.forEach((e) => {
                    set(project, item.path, e as never)
                })
                break
            }
            case "update": {
                set(project, item.path, item.data as never)
                break
            }
            case "batchUpdate": {
                projectEntityKeys.forEach((key) => {
                    if (project[key]) {
                        project[key].forEach((e) => {
                            if (
                                item.groupKey in e &&
                                (e as unknown as Record<string, unknown>)[item.groupKey] ===
                                    item.path.at(-1)
                            ) {
                                Object.assign(e, item.data)
                            }
                        })
                    }
                })
                break
            }
            case "batchEntitiesUpdate": {
                const entities = get(project, item.path as never) as { id: string }[]
                entities.forEach((e) => {
                    if (item.ids.includes(e["id"])) {
                        Object.assign(e, item.data)
                    }
                })
                break
            }
            case "delete": {
                del(project, item.path)
                break
            }
            case "replace": {
                set(project, item.path, item.data as never)
                break
            }
            default:
                // if you do not handle every case, this errors
                const _exhausitve: never = item
                return _exhausitve
        }
    })
}

const { actions } = slice
export const { reducer } = slice
export const {
    addEntitySocket,
    updateEntitySocket,
    deleteEntitySocket,
    batchAddEntitySocket,
    batchUpdateGroupSocket,
    batchUpdateEntitiesSocket,
    replaceEntitySocket,
    rejectCreate,
    rejectUpdate,
    handleIncomingSocketEvents,
} = actions
